// Generated by ReScript, PLEASE EDIT WITH CARE

import * as React from "react";
import * as Js_exn from "rescript/lib/es6/js_exn.js";
import * as Recoil from "recoil";
import * as Belt_Array from "rescript/lib/es6/belt_Array.js";
import * as Core__JSON from "@rescript/core/src/Core__JSON.bs.js";
import * as Caml_option from "rescript/lib/es6/caml_option.js";
import * as Core__Array from "@rescript/core/src/Core__Array.bs.js";
import * as Core__Option from "@rescript/core/src/Core__Option.bs.js";
import * as Belt_SortArray from "rescript/lib/es6/belt_SortArray.js";
import * as Caml_js_exceptions from "rescript/lib/es6/caml_js_exceptions.js";
import * as Modal$OrcaPaymentPage from "../Components/Modal.bs.js";
import * as Utils$OrcaPaymentPage from "../Utilities/Utils.bs.js";
import * as APIUtils$OrcaPaymentPage from "../Utilities/APIHelpers/APIUtils.bs.js";
import * as RenderIf$OrcaPaymentPage from "../Components/RenderIf.bs.js";
import * as EvmWallet$OrcaPaymentPage from "../Utilities/EvmWallet.bs.js";
import * as ApiEndpoint$OrcaPaymentPage from "../Utilities/ApiEndpoint.bs.js";
import * as RecoilAtoms$OrcaPaymentPage from "../Utilities/RecoilAtoms.bs.js";
import * as SolanaWallet$OrcaPaymentPage from "../Utilities/SolanaWallet.bs.js";
import * as QrCodeGenerator$OrcaPaymentPage from "../Utilities/QrCodeGenerator.bs.js";
import * as PaymentHelpersV2$OrcaPaymentPage from "../Utilities/PaymentHelpersV2.bs.js";
import * as CryptoCheckoutEnvironment$OrcaPaymentPage from "../Utilities/CryptoCheckoutEnvironment.bs.js";

function parseInvoiceStatus(str) {
  switch (str) {
    case "confirming" :
        return "Confirming";
    case "expired" :
        return "Expired";
    case "failed" :
        return "Failed";
    case "open" :
        return "Open";
    case "overpaid" :
        return "Overpaid";
    case "paid" :
        return "Paid";
    case "review_required" :
        return "ReviewRequired";
    case "tx_observed" :
        return "TxObserved";
    case "underpaid" :
        return "Underpaid";
    default:
      return "Unknown";
  }
}

function invoiceStatusLabel(st) {
  switch (st) {
    case "Open" :
        return "Waiting for payment…";
    case "TxObserved" :
        return "Transaction detected — awaiting confirmations";
    case "Confirming" :
        return "Confirming on-chain…";
    case "Paid" :
        return "Payment confirmed!";
    case "Underpaid" :
        return "Amount received is less than required";
    case "Overpaid" :
        return "Amount received is more than required";
    case "Expired" :
        return "Invoice expired";
    case "Failed" :
        return "Payment failed";
    case "ReviewRequired" :
        return "Payment under review…";
    case "Unknown" :
        return "Checking status…";
    
  }
}

function invoiceStatusColor(st) {
  switch (st) {
    case "Confirming" :
        return "#3b82f6";
    case "Paid" :
        return "#10b981";
    case "Expired" :
    case "Failed" :
        return "#ef4444";
    case "TxObserved" :
    case "Underpaid" :
    case "Overpaid" :
    case "ReviewRequired" :
        return "#f59e0b";
    case "Open" :
    case "Unknown" :
        return "#6b7280";
    
  }
}

function invoiceStatusStep(st) {
  switch (st) {
    case "TxObserved" :
        return 1;
    case "Paid" :
    case "Overpaid" :
        return 3;
    case "Confirming" :
    case "Underpaid" :
    case "ReviewRequired" :
        return 2;
    default:
      return 0;
  }
}

function isTerminalStatus(st) {
  switch (st) {
    case "Paid" :
    case "Overpaid" :
    case "Expired" :
    case "Failed" :
        return true;
    default:
      return false;
  }
}

function isSolanaChain(chain) {
  var c = chain.toLowerCase();
  if (c === "solana" || c === "solana-devnet" || c === "solana_devnet" || c.startsWith("solana:")) {
    return true;
  } else {
    return c.startsWith("solana-");
  }
}

function normalizeIsoUtc(s) {
  return s.trim().replace(/ ([+-]\d{2}:\d{2})(?::\d{2})?$/, "$1").replace(/ /, "T");
}

function getTimestampSeconds(dict, primaryKey, fallbackKey) {
  var raw = Core__Option.orElse(dict[primaryKey], dict[fallbackKey]);
  if (raw === undefined) {
    return 0.0;
  }
  var seconds = Core__JSON.Decode.$$float(raw);
  if (seconds !== undefined) {
    return seconds;
  }
  var str = Core__JSON.Decode.string(raw);
  if (str === undefined) {
    return 0.0;
  }
  if (str === "") {
    return 0.0;
  }
  var parsedMs = Date.parse(normalizeIsoUtc(str));
  if (parsedMs === parsedMs && parsedMs > 0.0) {
    return parsedMs / 1000.0;
  } else {
    return 0.0;
  }
}

function parseExecutionPlan(instruction) {
  var dict = Utils$OrcaPaymentPage.getDictFromJson(instruction);
  return Core__Option.map(Core__Option.flatMap(dict["steps"], Core__JSON.Decode.array), (function (steps) {
                return steps.map(function (step) {
                            var s = Utils$OrcaPaymentPage.getDictFromJson(step);
                            return {
                                    id: Utils$OrcaPaymentPage.getString(s, "id", ""),
                                    action: Utils$OrcaPaymentPage.getString(s, "action", ""),
                                    description: Utils$OrcaPaymentPage.getString(s, "description", ""),
                                    kind: Utils$OrcaPaymentPage.getString(s, "kind", "transaction"),
                                    status: Utils$OrcaPaymentPage.getString(s, "status", "incomplete"),
                                    call: Core__Option.getOr(s["call"], null)
                                  };
                          });
              }));
}

function parseCryptoActionInstruction(dict) {
  var actions = Core__Option.getOr(Core__Option.flatMap(dict["actions"], Core__JSON.Decode.array), []);
  return Core__Array.findMap(actions, (function (action) {
                var actionDict = Utils$OrcaPaymentPage.getDictFromJson(action);
                var match = Utils$OrcaPaymentPage.getString(actionDict, "type", "");
                if (match !== "wallet_execute") {
                  return ;
                }
                var instruction = Core__Option.getOr(actionDict["instruction"], null);
                var actionId = Utils$OrcaPaymentPage.getString(actionDict, "id", "");
                var ts = getTimestampSeconds(actionDict, "quote_expires_at", "quote_expires_at");
                var quoteExpiresAt = ts !== 0.0 ? ts : undefined;
                var sourceTokenDecimals = Core__Option.flatMap(Core__Option.flatMap(actionDict["source_token_decimals"], Core__JSON.Decode.$$float), (function (f) {
                        if (f > 0.0) {
                          return f | 0;
                        }
                        
                      }));
                var buyerPaysMinor = Core__Option.flatMap(Core__Option.flatMap(actionDict["buyer_pays_minor"], Core__JSON.Decode.$$float), (function (f) {
                        if (f > 0.0) {
                          return f;
                        }
                        
                      }));
                var merchantCreditMinor = Core__Option.flatMap(Core__Option.flatMap(actionDict["merchant_credit_minor"], Core__JSON.Decode.$$float), (function (f) {
                        if (f > 0.0) {
                          return f;
                        }
                        
                      }));
                if (actionId === "") {
                  return ;
                } else {
                  return {
                          actionId: actionId,
                          stepId: actionId,
                          status: Utils$OrcaPaymentPage.getString(actionDict, "status", "unknown"),
                          provider: Utils$OrcaPaymentPage.getString(actionDict, "provider", ""),
                          stepStatus: Utils$OrcaPaymentPage.getString(actionDict, "status", "unknown"),
                          sourceChain: Utils$OrcaPaymentPage.getString(actionDict, "source_chain", ""),
                          sourceAsset: Utils$OrcaPaymentPage.getString(actionDict, "source_asset", ""),
                          sourceAmountMinor: Utils$OrcaPaymentPage.getFloat(actionDict, "source_amount_minor", 0.0),
                          sourceTokenDecimals: sourceTokenDecimals,
                          instruction: instruction,
                          executionPlan: parseExecutionPlan(instruction),
                          quoteExpiresAt: quoteExpiresAt,
                          feeBearer: Utils$OrcaPaymentPage.getString(actionDict, "fee_bearer", ""),
                          buyerPaysMinor: buyerPaysMinor,
                          merchantCreditMinor: merchantCreditMinor
                        };
                }
              }));
}

function parseSolanaTransferActionId(dict) {
  var actions = Core__Option.getOr(Core__Option.flatMap(dict["actions"], Core__JSON.Decode.array), []);
  return Core__Array.findMap(actions, (function (action) {
                var actionDict = Utils$OrcaPaymentPage.getDictFromJson(action);
                if (Utils$OrcaPaymentPage.getString(actionDict, "type", "") !== "solana_transfer") {
                  return ;
                }
                var id = Utils$OrcaPaymentPage.getString(actionDict, "id", "");
                if (id === "") {
                  return ;
                } else {
                  return id;
                }
              }));
}

function parseAvailableRails(invDict) {
  return Core__Array.filterMap(Core__Option.getOr(Core__Option.flatMap(invDict["available_rails"], Core__JSON.Decode.array), []), (function (entry) {
                var d = Utils$OrcaPaymentPage.getDictFromJson(entry);
                var chain = Utils$OrcaPaymentPage.getString(d, "chain", "");
                var asset = Utils$OrcaPaymentPage.getString(d, "asset", "");
                if (chain === "" && asset === "") {
                  return ;
                }
                var tokenDecimals = Core__Option.flatMap(Core__Option.flatMap(d["token_decimals"], Core__JSON.Decode.$$float), (function (f) {
                        if (f > 0.0) {
                          return f | 0;
                        }
                        
                      }));
                var dn = Utils$OrcaPaymentPage.getString(d, "display_name", "");
                var displayName = dn === "" ? (
                    asset === "" ? chain : asset + " on " + chain
                  ) : dn;
                return {
                        chain: chain,
                        asset: asset,
                        tokenDecimals: tokenDecimals,
                        displayName: displayName,
                        isDefault: Utils$OrcaPaymentPage.getBool(d, "is_default", false)
                      };
              }));
}

function formatAmount(amountMinor, decimals) {
  var divisor = Belt_Array.reduce(Belt_Array.make(decimals, 10.0), 1.0, (function (a, b) {
          return a * b;
        }));
  var whole = amountMinor / divisor | 0;
  var fracInt = amountMinor % divisor | 0;
  var frac = fracInt.toString().padStart(decimals, "0");
  return whole.toString() + "." + frac;
}

function formatChainName(chain) {
  var other = chain.toLowerCase();
  switch (other) {
    case "arbitrum" :
        return "Arbitrum";
    case "avalanche" :
        return "Avalanche";
    case "base" :
        return "Base";
    case "base-sepolia" :
        return "Base Sepolia";
    case "bsc" :
        return "BSC";
    case "ethereum" :
        return "Ethereum";
    case "optimism" :
        return "Optimism";
    case "polygon" :
        return "Polygon";
    case "solana" :
        return "Solana";
    case "solana-devnet" :
        return "Solana Devnet";
    case "tron" :
        return "TRON";
    case "tron-nile" :
        return "TRON Nile";
    default:
      return other.slice(0, 1).toUpperCase() + other.slice(1);
  }
}

function qrImageUrl(data) {
  if (data === "") {
    return "";
  } else {
    return QrCodeGenerator$OrcaPaymentPage.toSvgDataUri(data);
  }
}

async function fetchInstructionStatus(clientSecret, publishableKey, paymentId, profileIdOpt, cryptoInvoiceIdOpt, handoffTokenOpt, handoffMerchantIdOpt, endpointOpt) {
  var profileId = profileIdOpt !== undefined ? profileIdOpt : "";
  var handoffToken = handoffTokenOpt !== undefined ? handoffTokenOpt : "";
  var handoffMerchantId = handoffMerchantIdOpt !== undefined ? handoffMerchantIdOpt : "";
  var endpoint = endpointOpt !== undefined ? endpointOpt : "";
  var baseUrl = endpoint === "" ? (
      publishableKey === "" ? window.location.origin : ApiEndpoint$OrcaPaymentPage.getApiEndPoint(publishableKey, undefined)
    ) : endpoint;
  var headerDict = handoffToken === "" ? Object.fromEntries(APIUtils$OrcaPaymentPage.v2ClientAuthHeaders(publishableKey, clientSecret, profileId)) : Object.fromEntries([
          [
            "Content-Type",
            "application/json"
          ],
          [
            "Authorization",
            "crypto-handoff-token=" + handoffToken
          ]
        ]);
  try {
    var newUrl = handoffToken === "" ? baseUrl + "/v2/payments/" + paymentId + "/crypto/invoice" : baseUrl + "/v2/payments/" + paymentId + "/crypto/invoice?t=" + handoffToken + (
        handoffMerchantId === "" ? "" : "&m=" + handoffMerchantId
      );
    var resp = await Utils$OrcaPaymentPage.fetchApi(newUrl, undefined, headerDict, "GET", undefined, Caml_option.some(publishableKey), undefined, undefined);
    var json = await resp.json();
    var dict = Utils$OrcaPaymentPage.getDictFromJson(json);
    var inv = dict["invoice"];
    if (inv === undefined) {
      return [
              "Unknown",
              undefined,
              parseCryptoActionInstruction(dict),
              parseSolanaTransferActionId(dict),
              undefined,
              undefined,
              undefined,
              undefined,
              undefined,
              undefined,
              undefined,
              undefined,
              []
            ];
    }
    var invDict = Utils$OrcaPaymentPage.getDictFromJson(inv);
    var st = parseInvoiceStatus(Utils$OrcaPaymentPage.getString(invDict, "status", "unknown"));
    var paymentUri = Core__Option.flatMap(Core__Option.flatMap(invDict["payment_uri"], Core__JSON.Decode.string), (function (s) {
            if (s === "") {
              return ;
            } else {
              return s;
            }
          }));
    var currentConfirmations = Core__Option.map(Core__Option.flatMap(invDict["current_confirmations"], Core__JSON.Decode.$$float), (function (f) {
            return f | 0;
          }));
    var requiredConfirmations = Core__Option.flatMap(Core__Option.flatMap(invDict["required_confirmations"], Core__JSON.Decode.$$float), (function (f) {
            if (f > 0.0) {
              return f | 0;
            }
            
          }));
    var address = Core__Option.flatMap(invDict["address"], Core__JSON.Decode.string);
    var chain = Core__Option.flatMap(invDict["chain"], Core__JSON.Decode.string);
    var asset = Core__Option.flatMap(invDict["asset"], Core__JSON.Decode.string);
    var amountMinor = Core__Option.flatMap(Core__Option.orElse(invDict["amount_minor"], invDict["expected_amount_minor"]), Core__JSON.Decode.$$float);
    var decimals = Core__Option.map(Core__Option.flatMap(invDict["decimals"], Core__JSON.Decode.$$float), (function (prim) {
            return prim | 0;
          }));
    var expiryAtSeconds = getTimestampSeconds(invDict, "expires_at", "expiry_at");
    var expiryAt = expiryAtSeconds > 0.0 ? expiryAtSeconds : undefined;
    var availableRails = parseAvailableRails(dict);
    return [
            st,
            paymentUri,
            parseCryptoActionInstruction(dict),
            parseSolanaTransferActionId(dict),
            currentConfirmations,
            requiredConfirmations,
            address,
            chain,
            asset,
            amountMinor,
            decimals,
            expiryAt,
            availableRails
          ];
  }
  catch (exn){
    return [
            "Unknown",
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            []
          ];
  }
}

async function submitCryptoActionTx(clientSecret, publishableKey, paymentId, profileIdOpt, actionId, txHash, handoffTokenOpt, handoffMerchantIdOpt, endpointOpt) {
  var profileId = profileIdOpt !== undefined ? profileIdOpt : "";
  var handoffToken = handoffTokenOpt !== undefined ? handoffTokenOpt : "";
  var handoffMerchantId = handoffMerchantIdOpt !== undefined ? handoffMerchantIdOpt : "";
  var endpoint = endpointOpt !== undefined ? endpointOpt : "";
  if (!(actionId !== "" && txHash !== "")) {
    return ;
  }
  var baseUrl = endpoint === "" ? (
      publishableKey === "" ? window.location.origin : ApiEndpoint$OrcaPaymentPage.getApiEndPoint(publishableKey, undefined)
    ) : endpoint;
  var completeUrl = baseUrl + "/v2/payments/" + paymentId + "/crypto/actions/" + actionId + "/complete" + (
    handoffToken === "" ? "" : "?t=" + handoffToken + (
        handoffMerchantId === "" ? "" : "&m=" + handoffMerchantId
      )
  );
  var headerDict = handoffToken === "" ? Object.fromEntries(APIUtils$OrcaPaymentPage.v2ClientAuthHeaders(publishableKey, clientSecret, profileId)) : Object.fromEntries([
          [
            "Content-Type",
            "application/json"
          ],
          [
            "Authorization",
            "crypto-handoff-token=" + handoffToken
          ]
        ]);
  var body = JSON.stringify(Utils$OrcaPaymentPage.getJsonFromArrayOfJson([[
              "transaction_id",
              txHash
            ]]));
  await Utils$OrcaPaymentPage.fetchApi(completeUrl, body, headerDict, "POST", undefined, Caml_option.some(publishableKey), undefined, undefined);
}

async function fetchSolanaPayTransaction(clientSecret, publishableKey, paymentId, profileIdOpt, actionId, account, handoffTokenOpt, handoffMerchantIdOpt, endpointOpt) {
  var profileId = profileIdOpt !== undefined ? profileIdOpt : "";
  var handoffToken = handoffTokenOpt !== undefined ? handoffTokenOpt : "";
  var handoffMerchantId = handoffMerchantIdOpt !== undefined ? handoffMerchantIdOpt : "";
  var endpoint = endpointOpt !== undefined ? endpointOpt : "";
  var baseUrl = endpoint === "" ? (
      publishableKey === "" ? window.location.origin : ApiEndpoint$OrcaPaymentPage.getApiEndPoint(publishableKey, undefined)
    ) : endpoint;
  var url = baseUrl + "/v2/payments/" + paymentId + "/crypto/actions/" + actionId + "/prepare" + (
    handoffToken === "" ? "" : "?t=" + handoffToken + (
        handoffMerchantId === "" ? "" : "&m=" + handoffMerchantId
      )
  );
  var headerDict = handoffToken === "" ? Object.fromEntries(APIUtils$OrcaPaymentPage.v2ClientAuthHeaders(publishableKey, clientSecret, profileId)) : Object.fromEntries([
          [
            "Content-Type",
            "application/json"
          ],
          [
            "Authorization",
            "crypto-handoff-token=" + handoffToken
          ]
        ]);
  var body = JSON.stringify(Utils$OrcaPaymentPage.getJsonFromArrayOfJson([[
              "wallet_address",
              account
            ]]));
  var resp = await Utils$OrcaPaymentPage.fetchApi(url, body, headerDict, "POST", undefined, Caml_option.some(publishableKey), undefined, undefined);
  var json = await resp.json();
  var dict = Utils$OrcaPaymentPage.getDictFromJson(json);
  return Utils$OrcaPaymentPage.getString(dict, "transaction", "");
}

async function refreshCryptoQuote(clientSecret, publishableKey, paymentId, profileIdOpt, handoffTokenOpt, handoffMerchantIdOpt, endpointOpt) {
  var profileId = profileIdOpt !== undefined ? profileIdOpt : "";
  var handoffToken = handoffTokenOpt !== undefined ? handoffTokenOpt : "";
  var handoffMerchantId = handoffMerchantIdOpt !== undefined ? handoffMerchantIdOpt : "";
  var endpoint = endpointOpt !== undefined ? endpointOpt : "";
  var baseUrl = endpoint === "" ? (
      publishableKey === "" ? window.location.origin : ApiEndpoint$OrcaPaymentPage.getApiEndPoint(publishableKey, undefined)
    ) : endpoint;
  var url = baseUrl + "/v2/payments/" + paymentId + "/crypto/quote/refresh" + (
    handoffToken === "" ? "" : "?t=" + handoffToken + (
        handoffMerchantId === "" ? "" : "&m=" + handoffMerchantId
      )
  );
  var headerDict = handoffToken === "" ? Object.fromEntries(APIUtils$OrcaPaymentPage.v2ClientAuthHeaders(publishableKey, clientSecret, profileId)) : Object.fromEntries([
          [
            "Content-Type",
            "application/json"
          ],
          [
            "Authorization",
            "crypto-handoff-token=" + handoffToken
          ]
        ]);
  try {
    var resp = await Utils$OrcaPaymentPage.fetchApi(url, "", headerDict, "POST", undefined, Caml_option.some(publishableKey), undefined, undefined);
    if (!resp.ok) {
      return ;
    }
    var json = await resp.json();
    var dict = Utils$OrcaPaymentPage.getDictFromJson(json);
    var ts = getTimestampSeconds(dict, "quote_expires_at", "quote_expires_at");
    var rqQuoteExpiresAt = ts !== 0.0 ? ts : undefined;
    return {
            rqQuoteExpiresAt: rqQuoteExpiresAt,
            rqSourceChain: Utils$OrcaPaymentPage.getString(dict, "source_chain", ""),
            rqSourceAsset: Utils$OrcaPaymentPage.getString(dict, "source_asset", ""),
            rqSourceAmountMinor: Utils$OrcaPaymentPage.getFloat(dict, "source_amount_minor", 0.0),
            rqTargetChain: Utils$OrcaPaymentPage.getString(dict, "target_chain", ""),
            rqTargetAsset: Utils$OrcaPaymentPage.getString(dict, "target_asset", ""),
            rqTargetAmountMinor: Utils$OrcaPaymentPage.getFloat(dict, "target_amount_minor", 0.0)
          };
  }
  catch (exn){
    return ;
  }
}

function safeDetectEnvironment() {
  try {
    return CryptoCheckoutEnvironment$OrcaPaymentPage.detect();
  }
  catch (exn){
    return {};
  }
}

function NativeCryptoDisplay(props) {
  var match = React.useState(function () {
        return "";
      });
  var setAddress = match[1];
  var address = match[0];
  var match$1 = React.useState(function () {
        return "";
      });
  var setChain = match$1[1];
  var chain = match$1[0];
  var match$2 = React.useState(function () {
        return "";
      });
  var setAsset = match$2[1];
  var asset = match$2[0];
  var match$3 = React.useState(function () {
        return 0.0;
      });
  var setAmountMinor = match$3[1];
  var amountMinor = match$3[0];
  var match$4 = React.useState(function () {
        return 6;
      });
  var setDecimals = match$4[1];
  var decimals = match$4[0];
  var match$5 = React.useState(function () {
        return false;
      });
  var setIsCopied = match$5[1];
  var match$6 = React.useState(function () {
        return 900000.0;
      });
  var setExpiryTime = match$6[1];
  var expiryTime = match$6[0];
  var match$7 = React.useState(function () {
        return false;
      });
  var setOpenModal = match$7[1];
  var openModal = match$7[0];
  var match$8 = React.useState(function () {
        return "";
      });
  var setReturnUrl = match$8[1];
  var returnUrl = match$8[0];
  var match$9 = React.useState(function () {
        return "";
      });
  var setClientSecret = match$9[1];
  var clientSecret = match$9[0];
  var match$10 = React.useState(function () {
        return "";
      });
  var setPublishableKey = match$10[1];
  var publishableKey = match$10[0];
  var match$11 = React.useState(function () {
        return "";
      });
  var setApiEndpoint = match$11[1];
  var apiEndpoint = match$11[0];
  var match$12 = React.useState(function () {
        return "";
      });
  var setHandoffToken = match$12[1];
  var handoffToken = match$12[0];
  var match$13 = React.useState(function () {
        return "";
      });
  var setHandoffMerchantId = match$13[1];
  var handoffMerchantId = match$13[0];
  var match$14 = React.useState(function () {
        return safeDetectEnvironment();
      });
  var setEnvironment = match$14[1];
  var match$15 = React.useState(function () {
        return "";
      });
  var setPaymentId = match$15[1];
  var paymentId = match$15[0];
  var match$16 = React.useState(function () {
        return "";
      });
  var setProfileId = match$16[1];
  var profileId = match$16[0];
  var match$17 = React.useState(function () {
        return "";
      });
  var setCryptoInvoiceId = match$17[1];
  var cryptoInvoiceId = match$17[0];
  var match$18 = React.useState(function () {
        return "Open";
      });
  var setInvoiceStatus = match$18[1];
  var invoiceStatus = match$18[0];
  var match$19 = React.useState(function () {
        
      });
  var setCurrentConfirmations = match$19[1];
  var match$20 = React.useState(function () {
        
      });
  var setRequiredConfirmations = match$20[1];
  var requiredConfirmations = match$20[0];
  var match$21 = React.useState(function () {
        
      });
  var setPaymentUri = match$21[1];
  var paymentUri = match$21[0];
  var match$22 = React.useState(function () {
        return false;
      });
  var setUseWalletNativeQr = match$22[1];
  var useWalletNativeQr = match$22[0];
  var match$23 = React.useState(function () {
        
      });
  var setRoutePlanInstruction = match$23[1];
  var routePlanInstruction = match$23[0];
  var match$24 = React.useState(function () {
        return 0.0;
      });
  var setQuoteExpiryTime = match$24[1];
  var quoteExpiryTime = match$24[0];
  var match$25 = React.useState(function () {
        return false;
      });
  var setQuoteExpired = match$25[1];
  var quoteExpired = match$25[0];
  var match$26 = React.useState(function () {
        
      });
  var setSelectedRail = match$26[1];
  var selectedRail = match$26[0];
  var emptyAvailableRails = [];
  var match$27 = React.useState(function () {
        return emptyAvailableRails;
      });
  var setAvailableRails = match$27[1];
  var availableRails = match$27[0];
  var match$28 = React.useState(function () {
        return false;
      });
  var setPayConfirmed = match$28[1];
  var match$29 = React.useState(function () {
        return "";
      });
  var setSettlementChain = match$29[1];
  var settlementChain = match$29[0];
  var match$30 = React.useState(function () {
        return "";
      });
  var setSettlementAsset = match$30[1];
  var settlementAsset = match$30[0];
  var customPodUri = Recoil.useRecoilValue(RecoilAtoms$OrcaPaymentPage.customPodUri);
  var copyTimeoutRef = React.useRef(undefined);
  var completedRef = React.useRef(false);
  var match$31 = React.useState(function () {
        return false;
      });
  var setIsSubmitting = match$31[1];
  var isSubmitting = match$31[0];
  var match$32 = React.useState(function () {
        
      });
  var setSubmitError = match$32[1];
  var submitError = match$32[0];
  var match$33 = React.useState(function () {
        return false;
      });
  var setIsExecutingRoute = match$33[1];
  var isExecutingRoute = match$33[0];
  var match$34 = React.useState(function () {
        
      });
  var setRouteExecutionMessage = match$34[1];
  var routeExecutionMessage = match$34[0];
  var match$35 = React.useState(function () {
        return false;
      });
  var setIsExecutingSolana = match$35[1];
  var isExecutingSolana = match$35[0];
  var match$36 = React.useState(function () {
        
      });
  var setSolanaMessage = match$36[1];
  var solanaMessage = match$36[0];
  var match$37 = React.useState(function () {
        return "";
      });
  var setSolanaSignature = match$37[1];
  var solanaSignature = match$37[0];
  var match$38 = React.useState(function () {
        return "";
      });
  var setSolanaActionId = match$38[1];
  var solanaActionId = match$38[0];
  var emptyStepResults = [];
  var match$39 = React.useState(function () {
        return emptyStepResults;
      });
  var setStepResults = match$39[1];
  var stepResults = match$39[0];
  var match$40 = React.useState(function () {
        return -1;
      });
  var setExecutingStepIndex = match$40[1];
  var executingStepIndex = match$40[0];
  var match$41 = React.useState(function () {
        return 0;
      });
  var setExecutingTotalSteps = match$41[1];
  var match$42 = React.useState(function () {
        return "";
      });
  var setExecutingStepAction = match$42[1];
  var isHandoffPage = CryptoCheckoutEnvironment$OrcaPaymentPage.routePaymentId() !== "";
  React.useEffect((function () {
          Utils$OrcaPaymentPage.messageParentWindow(undefined, [[
                  "iframeMountedCallback",
                  true
                ]]);
          var handle = function (ev) {
            var handleAsync = async function () {
              var json = Utils$OrcaPaymentPage.safeParse(ev.data);
              var dict = Utils$OrcaPaymentPage.getDictFromJson(json);
              if (!Core__Option.isSome(dict["fullScreenIframeMounted"])) {
                return ;
              }
              var meta = Utils$OrcaPaymentPage.getDictFromJson(Utils$OrcaPaymentPage.getJsonObjectFromDict(dict, "metadata"));
              var addr = Utils$OrcaPaymentPage.getString(meta, "address", "");
              var ch = Utils$OrcaPaymentPage.getString(meta, "chain", "");
              var ast = Utils$OrcaPaymentPage.getString(meta, "asset", "");
              var amt = Utils$OrcaPaymentPage.getFloat(meta, "amountMinor", 0.0);
              var dec = Utils$OrcaPaymentPage.getInt(meta, "decimals", 6);
              var secret = Utils$OrcaPaymentPage.getString(meta, "clientSecret", "");
              var pid = Utils$OrcaPaymentPage.getString(meta, "paymentId", "");
              var profId = Utils$OrcaPaymentPage.getString(meta, "profileId", "");
              var invId = Utils$OrcaPaymentPage.getString(meta, "cryptoInvoiceId", "");
              var pubKey = Utils$OrcaPaymentPage.getString(meta, "publishableKey", "");
              var url = Utils$OrcaPaymentPage.getString(meta, "url", "");
              var endpoint = Utils$OrcaPaymentPage.getString(meta, "endpoint", "");
              var token = Utils$OrcaPaymentPage.getString(meta, "handoffToken", "");
              var merchantId = Utils$OrcaPaymentPage.getString(meta, "handoffMerchantId", "");
              setAddress(function (param) {
                    return addr;
                  });
              setChain(function (param) {
                    return ch;
                  });
              setAsset(function (param) {
                    return ast;
                  });
              setAmountMinor(function (param) {
                    return amt;
                  });
              setDecimals(function (param) {
                    return dec;
                  });
              setClientSecret(function (param) {
                    return secret;
                  });
              setPaymentId(function (param) {
                    return pid;
                  });
              setProfileId(function (param) {
                    return profId;
                  });
              setCryptoInvoiceId(function (param) {
                    return invId;
                  });
              setPublishableKey(function (param) {
                    return pubKey;
                  });
              setApiEndpoint(function (param) {
                    return endpoint;
                  });
              setHandoffToken(function (param) {
                    return token;
                  });
              setHandoffMerchantId(function (param) {
                    return merchantId;
                  });
              setReturnUrl(function (param) {
                    return url;
                  });
              var expAt = Utils$OrcaPaymentPage.getFloat(meta, "expiryAt", 0.0);
              if (expAt > 0.0) {
                var remaining = expAt * 1000.0 - Date.now();
                setExpiryTime(function (param) {
                      if (remaining > 0.0) {
                        return remaining;
                      } else {
                        return 0.0;
                      }
                    });
              }
              return setOpenModal(function (param) {
                          return true;
                        });
            };
            handleAsync();
          };
          window.addEventListener("message", handle);
          return (function () {
                    window.removeEventListener("message", handle);
                  });
        }), []);
  React.useEffect((function () {
          var pid = CryptoCheckoutEnvironment$OrcaPaymentPage.routePaymentId();
          if (pid !== "") {
            setPaymentId(function (param) {
                  return pid;
                });
            setHandoffToken(function (param) {
                  return CryptoCheckoutEnvironment$OrcaPaymentPage.queryParam("t");
                });
            setHandoffMerchantId(function (param) {
                  return CryptoCheckoutEnvironment$OrcaPaymentPage.queryParam("m");
                });
            setClientSecret(function (param) {
                  return CryptoCheckoutEnvironment$OrcaPaymentPage.queryParam("clientSecret");
                });
            setPublishableKey(function (param) {
                  return CryptoCheckoutEnvironment$OrcaPaymentPage.queryParam("publishableKey");
                });
            setProfileId(function (param) {
                  return CryptoCheckoutEnvironment$OrcaPaymentPage.queryParam("profileId");
                });
            setApiEndpoint(function (param) {
                  return CryptoCheckoutEnvironment$OrcaPaymentPage.queryParam("endpoint");
                });
            setReturnUrl(function (param) {
                  return CryptoCheckoutEnvironment$OrcaPaymentPage.queryParam("return_url");
                });
            setOpenModal(function (param) {
                  return true;
                });
          }
          
        }), []);
  React.useEffect((function () {
          setEnvironment(function (param) {
                return safeDetectEnvironment();
              });
          return CryptoCheckoutEnvironment$OrcaPaymentPage.addEip6963Listener(function (env) {
                      setEnvironment(function (param) {
                            return env;
                          });
                    });
        }), []);
  var countdownIntervalRef = React.useRef(undefined);
  React.useEffect((function () {
          var id = countdownIntervalRef.current;
          if (id !== undefined) {
            clearInterval(Caml_option.valFromOption(id));
            countdownIntervalRef.current = undefined;
          }
          if (!(openModal && !isTerminalStatus(invoiceStatus))) {
            return ;
          }
          var tickId = setInterval((function () {
                  setExpiryTime(function (prev) {
                        return prev - 1000.0;
                      });
                }), 1000);
          countdownIntervalRef.current = Caml_option.some(tickId);
          return (function () {
                    clearInterval(tickId);
                    countdownIntervalRef.current = undefined;
                  });
        }), [
        openModal,
        isTerminalStatus(invoiceStatus)
      ]);
  React.useEffect((function () {
          if (expiryTime <= 0.0) {
            setInvoiceStatus(function (prev) {
                  switch (prev) {
                    case "Paid" :
                    case "Overpaid" :
                        return prev;
                    default:
                      return "Expired";
                  }
                });
          }
          
        }), [expiryTime]);
  React.useEffect((function () {
          if (!(openModal && quoteExpiryTime > 0.0 && !isTerminalStatus(invoiceStatus) && !quoteExpired)) {
            return ;
          }
          var tickId = setInterval((function () {
                  setQuoteExpiryTime(function (prev) {
                        return prev - 1000.0;
                      });
                }), 1000);
          return (function () {
                    clearInterval(tickId);
                  });
        }), [
        openModal,
        quoteExpiryTime > 0.0,
        isTerminalStatus(invoiceStatus),
        quoteExpired
      ]);
  React.useEffect((function () {
          if (quoteExpiryTime > 0.0 && quoteExpiryTime < 1000.0 && !quoteExpired) {
            setQuoteExpired(function (param) {
                  return true;
                });
            setQuoteExpiryTime(function (param) {
                  return 0.0;
                });
          }
          
        }), [quoteExpiryTime]);
  React.useEffect((function () {
          if (clientSecret === "" && handoffToken === "" || paymentId === "" || isTerminalStatus(invoiceStatus)) {
            return ;
          }
          var cancelled = {
            contents: false
          };
          var poll = async function () {
            if (cancelled.contents) {
              return ;
            }
            var match = await fetchInstructionStatus(clientSecret, publishableKey, paymentId, profileId, cryptoInvoiceId, handoffToken, handoffMerchantId, apiEndpoint);
            if (cancelled.contents) {
              return ;
            }
            var fetchedAvailableRails = match[12];
            var fetchedExpiryAt = match[11];
            var fetchedDecimals = match[10];
            var fetchedAmountMinor = match[9];
            var fetchedAsset = match[8];
            var fetchedChain = match[7];
            var fetchedAddress = match[6];
            var requiredConfs = match[5];
            var currentConfs = match[4];
            var fetchedSolanaActionId = match[3];
            var routePlan = match[2];
            var uri = match[1];
            var st = match[0];
            setInvoiceStatus(function (param) {
                  return st;
                });
            if (fetchedAddress !== undefined && fetchedAddress !== "") {
              setAddress(function (param) {
                    return fetchedAddress;
                  });
            }
            if (fetchedChain !== undefined && fetchedChain !== "") {
              setChain(function (param) {
                    return fetchedChain;
                  });
              setSettlementChain(function (param) {
                    return fetchedChain;
                  });
            }
            if (fetchedAsset !== undefined && fetchedAsset !== "") {
              setAsset(function (param) {
                    return fetchedAsset;
                  });
              setSettlementAsset(function (param) {
                    return fetchedAsset;
                  });
            }
            if (fetchedAmountMinor !== undefined && fetchedAmountMinor > 0.0) {
              setAmountMinor(function (param) {
                    return fetchedAmountMinor;
                  });
            }
            if (fetchedDecimals !== undefined && fetchedDecimals > 0) {
              setDecimals(function (param) {
                    return fetchedDecimals;
                  });
            }
            if (fetchedExpiryAt !== undefined && fetchedExpiryAt > 0.0) {
              var remaining = fetchedExpiryAt * 1000.0 - Date.now();
              setExpiryTime(function (param) {
                    if (remaining > 0.0) {
                      return remaining;
                    } else {
                      return 0.0;
                    }
                  });
            }
            if (fetchedAvailableRails.length > 0) {
              setAvailableRails(function (param) {
                    return fetchedAvailableRails;
                  });
            }
            if (currentConfs !== undefined) {
              setCurrentConfirmations(function (param) {
                    return currentConfs;
                  });
            }
            if (requiredConfs !== undefined) {
              setRequiredConfirmations(function (param) {
                    return requiredConfs;
                  });
            }
            if (uri !== undefined) {
              setPaymentUri(function (param) {
                    return uri;
                  });
            }
            if (routePlan !== undefined) {
              setRoutePlanInstruction(function (param) {
                    return routePlan;
                  });
              var qExp = routePlan.quoteExpiresAt;
              if (qExp !== undefined && qExp > 0.0) {
                var remaining$1 = qExp * 1000.0 - Date.now();
                if (remaining$1 > 0.0) {
                  setQuoteExpiryTime(function (param) {
                        return remaining$1;
                      });
                  setQuoteExpired(function (param) {
                        return false;
                      });
                } else {
                  setQuoteExpiryTime(function (param) {
                        return 0.0;
                      });
                  setQuoteExpired(function (param) {
                        return true;
                      });
                }
              }
              if (routePlan.sourceChain !== "") {
                setChain(function (param) {
                      return routePlan.sourceChain;
                    });
              }
              if (routePlan.sourceAsset !== "") {
                setAsset(function (param) {
                      return routePlan.sourceAsset;
                    });
              }
              if (routePlan.sourceAmountMinor > 0.0) {
                setAmountMinor(function (param) {
                      return routePlan.sourceAmountMinor;
                    });
              }
              var steps = routePlan.executionPlan;
              if (steps !== undefined) {
                steps.forEach(function (step) {
                      var callDict = Utils$OrcaPaymentPage.getDictFromJson(step.call);
                      if (Utils$OrcaPaymentPage.getString(callDict, "mode", "") !== "deposit_address") {
                        return ;
                      }
                      var depositAddress = Utils$OrcaPaymentPage.getString(callDict, "address", "");
                      if (depositAddress !== "") {
                        return setAddress(function (param) {
                                    return depositAddress;
                                  });
                      }
                      
                    });
              }
              
            }
            if (fetchedSolanaActionId !== undefined) {
              setSolanaActionId(function (param) {
                    return fetchedSolanaActionId;
                  });
            }
            if (!(isTerminalStatus(st) && !completedRef.current)) {
              if (!isTerminalStatus(st)) {
                await Utils$OrcaPaymentPage.delay(4000);
                if (!cancelled.contents) {
                  return await poll();
                } else {
                  return ;
                }
              } else {
                return ;
              }
            }
            completedRef.current = true;
            var res = await PaymentHelpersV2$OrcaPaymentPage.retrievePaymentIntentV2(paymentId, clientSecret, publishableKey, profileId, customPodUri);
            Modal$OrcaPaymentPage.close(setOpenModal);
            return Utils$OrcaPaymentPage.postSubmitResponse(res, returnUrl);
          };
          poll();
          return (function () {
                    cancelled.contents = true;
                  });
        }), [
        clientSecret,
        invoiceStatusStep(invoiceStatus).toString(),
        publishableKey,
        paymentId,
        cryptoInvoiceId,
        profileId,
        handoffToken,
        apiEndpoint
      ]);
  var expiryString = React.useMemo((function () {
          var totalSec = Math.abs(expiryTime / 1000.0 | 0);
          var mins = (totalSec / 60 | 0).toString();
          var secs = (totalSec % 60).toString().padStart(2, "0");
          return mins + ":" + secs;
        }), [expiryTime]);
  var quoteExpiryString = React.useMemo((function () {
          var totalSec = Math.abs(quoteExpiryTime / 1000.0 | 0);
          var mins = (totalSec / 60 | 0).toString();
          var secs = (totalSec % 60).toString().padStart(2, "0");
          return mins + ":" + secs;
        }), [quoteExpiryTime]);
  var amountDisplay = React.useMemo((function () {
          return formatAmount(amountMinor, decimals);
        }), [
        amountMinor.toString(),
        decimals.toString()
      ]);
  var statusColor = invoiceStatusColor(invoiceStatus);
  var step = invoiceStatusStep(invoiceStatus);
  var isTerminal = isTerminalStatus(invoiceStatus);
  var hasRoutePlanInstruction = Core__Option.isSome(routePlanInstruction);
  var hasDepositAddressInstruction;
  if (routePlanInstruction !== undefined) {
    var eps = routePlanInstruction.executionPlan;
    hasDepositAddressInstruction = eps !== undefined ? Belt_Array.some(eps, (function (s) {
              return Utils$OrcaPaymentPage.getString(Utils$OrcaPaymentPage.getDictFromJson(s.call), "mode", "") === "deposit_address";
            })) : false;
  } else {
    hasDepositAddressInstruction = false;
  }
  var envDict = Utils$OrcaPaymentPage.getDictFromJson(match$14[0]);
  var envIsMobile = Utils$OrcaPaymentPage.getBool(envDict, "isMobile", false);
  var envIsCrossOriginIframe = Utils$OrcaPaymentPage.getBool(envDict, "isCrossOriginIframe", false);
  var envHasProvider = Utils$OrcaPaymentPage.getBool(envDict, "hasProvider", false);
  var envCanConnectWallet = Utils$OrcaPaymentPage.getBool(envDict, "canConnectWallet", false);
  var handoffLink = CryptoCheckoutEnvironment$OrcaPaymentPage.handoffUrl(paymentId, handoffToken);
  var needsHandoff = hasRoutePlanInstruction && !envCanConnectWallet;
  var showMobileIframeContinue = envIsMobile && envIsCrossOriginIframe && needsHandoff && handoffLink !== "";
  var showWalletLauncher = isHandoffPage && envIsMobile && !envHasProvider && !envIsCrossOriginIframe && !isTerminal;
  var showHandoffQr = needsHandoff && !showMobileIframeContinue && !showWalletLauncher && handoffLink !== "";
  var directTransferVisible = !hasRoutePlanInstruction || hasDepositAddressInstruction;
  var hasWalletNativeQr = Core__Option.isSome(paymentUri);
  var isConversion = hasRoutePlanInstruction && (
    routePlanInstruction !== undefined ? routePlanInstruction.sourceChain !== "" || routePlanInstruction.sourceAsset !== "" : false
  );
  var hasAvailableRails = availableRails.length > 0;
  var railLabelMap = {};
  var railOptions;
  if (hasAvailableRails) {
    var sorted = Belt_SortArray.stableSortBy(availableRails, (function (a, b) {
            var match = a.isDefault;
            var match$1 = b.isDefault;
            if (match) {
              if (match$1) {
                return 0;
              } else {
                return -1;
              }
            } else if (match$1) {
              return 1;
            } else {
              return 0;
            }
          }));
    railOptions = sorted.map(function (r) {
          if (r.displayName.trim() !== "") {
            railLabelMap[r.chain + " " + r.asset] = r.displayName;
          }
          return [
                  r.chain,
                  r.asset
                ];
        });
  } else {
    var opts = [];
    if (settlementChain !== "" || settlementAsset !== "") {
      opts.push([
            settlementChain,
            settlementAsset
          ]);
    }
    if (routePlanInstruction !== undefined && (routePlanInstruction.sourceChain !== "" || routePlanInstruction.sourceAsset !== "")) {
      var exists = Belt_Array.some(opts, (function (param) {
              if (param[0] === routePlanInstruction.sourceChain) {
                return param[1] === routePlanInstruction.sourceAsset;
              } else {
                return false;
              }
            }));
      if (!exists) {
        opts.push([
              routePlanInstruction.sourceChain,
              routePlanInstruction.sourceAsset
            ]);
      }
      
    }
    railOptions = opts;
  }
  var railLabel = function (param) {
    var a = param[1];
    var c = param[0];
    var dn = railLabelMap[c + " " + a];
    if (dn !== undefined && dn.trim() !== "") {
      return dn;
    }
    if (c === "") {
      if (a === "") {
        return "—";
      } else {
        return a;
      }
    } else if (a === "") {
      return formatChainName(c);
    } else {
      return a + " · " + formatChainName(c);
    }
  };
  var effectiveRail = selectedRail !== undefined ? selectedRail : railOptions[0];
  var showRailSelector = railOptions.length > 1 && !isTerminal && !match$28[0];
  var qrData;
  var exit = 0;
  if (paymentUri !== undefined && showHandoffQr) {
    qrData = handoffLink;
  } else {
    exit = 1;
  }
  if (exit === 1) {
    qrData = showHandoffQr ? handoffLink : (
        paymentUri !== undefined && (isSolanaChain(chain) || useWalletNativeQr) ? paymentUri : address
      );
  }
  var qrSrc = React.useMemo((function () {
          if (qrData !== "") {
            return qrImageUrl(qrData);
          } else {
            return "";
          }
        }), [qrData]);
  var confirmationProgress;
  if (requiredConfirmations !== undefined && requiredConfirmations > 0) {
    var current = Core__Option.getOr(match$19[0], 0);
    var shown = current > requiredConfirmations ? requiredConfirmations : current;
    confirmationProgress = [
      shown,
      requiredConfirmations
    ];
  } else {
    confirmationProgress = undefined;
  }
  var statusLabel;
  statusLabel = invoiceStatus === "Confirming" && confirmationProgress !== undefined ? invoiceStatusLabel(invoiceStatus) + " (" + confirmationProgress[0].toString() + "/" + confirmationProgress[1].toString() + ")" : invoiceStatusLabel(invoiceStatus);
  var showSolanaPay = isSolanaChain(chain) && !isTerminal && !hasRoutePlanInstruction && !showRailSelector && solanaActionId !== "";
  var handleCopy = function (param) {
    Utils$OrcaPaymentPage.messageParentWindow(undefined, [
          [
            "copy",
            true
          ],
          [
            "copyDetails",
            address
          ]
        ]);
    var id = copyTimeoutRef.current;
    if (id !== undefined) {
      clearTimeout(Caml_option.valFromOption(id));
    }
    setIsCopied(function (param) {
          return true;
        });
    var id$1 = setTimeout((function () {
            setIsCopied(function (param) {
                  return false;
                });
            copyTimeoutRef.current = undefined;
          }), 1500);
    copyTimeoutRef.current = Caml_option.some(id$1);
  };
  var handleCopyAmount = function (param) {
    Utils$OrcaPaymentPage.messageParentWindow(undefined, [
          [
            "copy",
            true
          ],
          [
            "copyDetails",
            amountDisplay
          ]
        ]);
  };
  var handleContinueOnDevice = function (param) {
    CryptoCheckoutEnvironment$OrcaPaymentPage.continueOnTop(handoffLink);
  };
  var handleConfirmPay = function (param) {
    setPayConfirmed(function (param) {
          return true;
        });
  };
  var handleRefreshQuote = async function () {
    var result = await refreshCryptoQuote(clientSecret, publishableKey, paymentId, profileId, handoffToken, handoffMerchantId, apiEndpoint);
    if (result === undefined) {
      return ;
    }
    var qExp = result.rqQuoteExpiresAt;
    if (qExp !== undefined && qExp > 0.0) {
      var remaining = qExp * 1000.0 - Date.now();
      if (remaining > 0.0) {
        setQuoteExpiryTime(function (param) {
              return remaining;
            });
        setQuoteExpired(function (param) {
              return false;
            });
      } else {
        setQuoteExpired(function (param) {
              return true;
            });
      }
    }
    if (result.rqSourceChain !== "" || result.rqSourceAsset !== "" || result.rqSourceAmountMinor > 0.0) {
      return setRoutePlanInstruction(function (prev) {
                  if (prev === undefined) {
                    return ;
                  }
                  var q = result.rqQuoteExpiresAt;
                  return {
                          actionId: prev.actionId,
                          stepId: prev.stepId,
                          status: prev.status,
                          provider: prev.provider,
                          stepStatus: prev.stepStatus,
                          sourceChain: result.rqSourceChain !== "" ? result.rqSourceChain : prev.sourceChain,
                          sourceAsset: result.rqSourceAsset !== "" ? result.rqSourceAsset : prev.sourceAsset,
                          sourceAmountMinor: result.rqSourceAmountMinor > 0.0 ? result.rqSourceAmountMinor : prev.sourceAmountMinor,
                          sourceTokenDecimals: prev.sourceTokenDecimals,
                          instruction: prev.instruction,
                          executionPlan: prev.executionPlan,
                          quoteExpiresAt: q !== undefined ? q : prev.quoteExpiresAt,
                          feeBearer: prev.feeBearer,
                          buyerPaysMinor: prev.buyerPaysMinor,
                          merchantCreditMinor: prev.merchantCreditMinor
                        };
                });
    }
    
  };
  var handleDone = async function () {
    setSubmitError(function (param) {
          
        });
    setIsSubmitting(function (param) {
          return true;
        });
    try {
      var res = await PaymentHelpersV2$OrcaPaymentPage.retrievePaymentIntentV2(paymentId, clientSecret, publishableKey, profileId, customPodUri);
      if (!completedRef.current) {
        completedRef.current = true;
        Modal$OrcaPaymentPage.close(setOpenModal);
        return Utils$OrcaPaymentPage.postSubmitResponse(res, returnUrl);
      } else {
        return ;
      }
    }
    catch (raw_e){
      var e = Caml_js_exceptions.internalToOCamlException(raw_e);
      console.error("NativeCryptoDisplay done:", e);
      setIsSubmitting(function (param) {
            return false;
          });
      return setSubmitError(function (param) {
                  return "Couldn't confirm payment yet. Please try again.";
                });
    }
  };
  var handleExecuteSolana = async function () {
    setSolanaMessage(function (param) {
          
        });
    setSolanaSignature(function (param) {
          return "";
        });
    setIsExecutingSolana(function (param) {
          return true;
        });
    if (solanaActionId === "") {
      setSolanaMessage(function (param) {
            return "Solana payment isn't ready yet — please wait a moment and try again.";
          });
      return setIsExecutingSolana(function (param) {
                  return false;
                });
    }
    try {
      var connectRes = await SolanaWallet$OrcaPaymentPage.connectSolanaWallet();
      var account = Utils$OrcaPaymentPage.getString(Utils$OrcaPaymentPage.getDictFromJson(connectRes), "account", "");
      if (account === "") {
        setSolanaMessage(function (param) {
              return "Couldn't read your Solana wallet address. Please reconnect.";
            });
        return setIsExecutingSolana(function (param) {
                    return false;
                  });
      }
      var base64Tx = await fetchSolanaPayTransaction(clientSecret, publishableKey, paymentId, profileId, solanaActionId, account, handoffToken, handoffMerchantId, apiEndpoint);
      if (base64Tx === "") {
        setSolanaMessage(function (param) {
              return "Couldn't build the Solana transaction. Please try again.";
            });
        return setIsExecutingSolana(function (param) {
                    return false;
                  });
      }
      var signRes = await SolanaWallet$OrcaPaymentPage.signAndSendBase64Tx(base64Tx, chain);
      var sig = Utils$OrcaPaymentPage.getString(Utils$OrcaPaymentPage.getDictFromJson(signRes), "txHash", "");
      if (sig !== "") {
        await submitCryptoActionTx(clientSecret, publishableKey, paymentId, profileId, solanaActionId, sig, handoffToken, handoffMerchantId, apiEndpoint);
      }
      setSolanaSignature(function (param) {
            return sig;
          });
      setSolanaMessage(function (param) {
            return sig === "" ? "Transaction sent. Waiting for on-chain confirmation…" : "Sent! Signature: " + sig + ". Waiting for on-chain confirmation…";
          });
      return setIsExecutingSolana(function (param) {
                  return false;
                });
    }
    catch (raw_exn){
      var exn = Caml_js_exceptions.internalToOCamlException(raw_exn);
      if (exn.RE_EXN_ID === Js_exn.$$Error) {
        var exn$1 = exn._1;
        console.error("NativeCryptoDisplay solana execution:", exn$1);
        var m = exn$1.message;
        var msg = m !== undefined && m !== "" ? m : "Solana wallet execution failed. Check your wallet and try again.";
        setSolanaMessage(function (param) {
              return msg;
            });
        return setIsExecutingSolana(function (param) {
                    return false;
                  });
      }
      console.error("NativeCryptoDisplay solana execution:", exn);
      setSolanaMessage(function (param) {
            return "Solana wallet execution failed. Check your wallet and try again.";
          });
      return setIsExecutingSolana(function (param) {
                  return false;
                });
    }
  };
  var handleExecuteRoute = async function (plan) {
    setRouteExecutionMessage(function (param) {
          
        });
    setIsExecutingRoute(function (param) {
          return true;
        });
    setStepResults(function (param) {
          return emptyStepResults;
        });
    setExecutingStepIndex(function (param) {
          return -1;
        });
    setExecutingTotalSteps(function (param) {
          return 0;
        });
    setExecutingStepAction(function (param) {
          return "";
        });
    var awaitParentExecution = function (actionId, instruction) {
      return new Promise((function (resolve, _reject) {
                    var timeoutId = {
                      contents: undefined
                    };
                    var listenerRef = {
                      contents: undefined
                    };
                    var cleanup = function () {
                      var id = timeoutId.contents;
                      if (id !== undefined) {
                        clearTimeout(Caml_option.valFromOption(id));
                      }
                      var fn = listenerRef.contents;
                      if (fn !== undefined) {
                        window.removeEventListener("message", fn);
                        return ;
                      }
                      
                    };
                    var handle = function (ev) {
                      var dict = Utils$OrcaPaymentPage.getDictFromJson(Utils$OrcaPaymentPage.safeParse(ev.data));
                      if (Core__Option.isSome(dict["executeStepsResult"])) {
                        cleanup();
                        return resolve(Utils$OrcaPaymentPage.safeParse(ev.data));
                      }
                      
                    };
                    listenerRef.contents = handle;
                    window.addEventListener("message", handle);
                    Utils$OrcaPaymentPage.messageParentWindow(undefined, [
                          [
                            "executeSteps",
                            true
                          ],
                          [
                            "actionId",
                            actionId
                          ],
                          [
                            "instruction",
                            instruction
                          ]
                        ]);
                    var tid = setTimeout((function () {
                            cleanup();
                            resolve(null);
                          }), 60000);
                    timeoutId.contents = Caml_option.some(tid);
                  }));
    };
    var steps = plan.executionPlan;
    if (steps !== undefined) {
      var activeSteps = Belt_Array.keep(steps, (function (s) {
              if (s.kind === "transaction") {
                return s.status === "incomplete";
              } else {
                return false;
              }
            }));
      if (activeSteps.length === 0 && Belt_Array.every(steps, (function (s) {
                return s.status === "complete";
              }))) {
        setRouteExecutionMessage(function (param) {
              return "All steps are already complete.";
            });
        return setIsExecutingRoute(function (param) {
                    return false;
                  });
      }
      var resultJson = await awaitParentExecution(plan.actionId, plan.instruction);
      var resultDict = Utils$OrcaPaymentPage.getDictFromJson(resultJson);
      var useBuiltin = resultJson === null;
      var match;
      if (useBuiltin) {
        var onProgress = function (idx, total, _stepId, action) {
          setExecutingStepIndex(function (param) {
                return idx;
              });
          setExecutingTotalSteps(function (param) {
                return total;
              });
          setExecutingStepAction(function (param) {
                return action;
              });
        };
        try {
          var res = await EvmWallet$OrcaPaymentPage.executeExecutionPlan(plan.instruction, onProgress);
          var rd = Utils$OrcaPaymentPage.getDictFromJson(res);
          var h = Utils$OrcaPaymentPage.getString(rd, "txHash", "");
          var raw = Core__Option.getOr(Core__Option.flatMap(rd["results"], Core__JSON.Decode.array), []);
          var p = raw.map(function (r) {
                var d = Utils$OrcaPaymentPage.getDictFromJson(r);
                return [
                        Utils$OrcaPaymentPage.getString(d, "stepId", ""),
                        Utils$OrcaPaymentPage.getString(d, "txHash", ""),
                        Utils$OrcaPaymentPage.getBool(d, "skipped", false),
                        Core__Option.flatMap(d["error"], Core__JSON.Decode.string)
                      ];
              });
          match = [
            h,
            p,
            Belt_Array.some(p, (function (param) {
                    if (Core__Option.isSome(param[3])) {
                      return !param[2];
                    } else {
                      return false;
                    }
                  }))
          ];
        }
        catch (raw_e){
          var e = Caml_js_exceptions.internalToOCamlException(raw_e);
          console.error("NativeCryptoDisplay fallback execution:", e);
          setRouteExecutionMessage(function (param) {
                return "Wallet execution failed. Check wallet connection and network.";
              });
          setIsExecutingRoute(function (param) {
                return false;
              });
          setExecutingStepIndex(function (param) {
                return -1;
              });
          match = [
            "",
            [],
            false
          ];
        }
      } else {
        var errMsg = Core__Option.flatMap(resultDict["error"], Core__JSON.Decode.string);
        if (errMsg !== undefined) {
          setRouteExecutionMessage(function (param) {
                return errMsg;
              });
          setIsExecutingRoute(function (param) {
                return false;
              });
          setExecutingStepIndex(function (param) {
                return -1;
              });
          match = [
            "",
            [],
            true
          ];
        } else {
          var h$1 = Utils$OrcaPaymentPage.getString(resultDict, "txHash", "");
          var raw$1 = Core__Option.getOr(Core__Option.flatMap(resultDict["results"], Core__JSON.Decode.array), []);
          var p$1 = raw$1.map(function (r) {
                var d = Utils$OrcaPaymentPage.getDictFromJson(r);
                return [
                        Utils$OrcaPaymentPage.getString(d, "stepId", ""),
                        Utils$OrcaPaymentPage.getString(d, "txHash", ""),
                        Utils$OrcaPaymentPage.getBool(d, "skipped", false),
                        Core__Option.flatMap(d["error"], Core__JSON.Decode.string)
                      ];
              });
          match = [
            h$1,
            p$1,
            Belt_Array.some(p$1, (function (param) {
                    if (Core__Option.isSome(param[3])) {
                      return !param[2];
                    } else {
                      return false;
                    }
                  }))
          ];
        }
      }
      var parsedResults = match[1];
      var txHash = match[0];
      if (parsedResults.length > 0) {
        setStepResults(function (param) {
              return parsedResults;
            });
      }
      if (!match[2]) {
        var baseMsg = txHash === "" ? "EVM steps completed." : "Completed. Last tx: " + txHash;
        setRouteExecutionMessage(function (param) {
              return baseMsg;
            });
        if (txHash !== "") {
          try {
            await submitCryptoActionTx(clientSecret, publishableKey, paymentId, profileId, plan.actionId, txHash, handoffToken, handoffMerchantId, apiEndpoint);
          }
          catch (raw_submitError){
            var submitError = Caml_js_exceptions.internalToOCamlException(raw_submitError);
            console.error("NativeCryptoDisplay route submit:", submitError);
          }
        }
        
      }
      setIsExecutingRoute(function (param) {
            return false;
          });
      return setExecutingStepIndex(function (param) {
                  return -1;
                });
    }
    var resultJson$1 = await awaitParentExecution(plan.actionId, plan.instruction);
    var resultDict$1 = Utils$OrcaPaymentPage.getDictFromJson(resultJson$1);
    var useBuiltin$1 = resultJson$1 === null;
    var match$1;
    if (useBuiltin$1) {
      try {
        var res$1 = await EvmWallet$OrcaPaymentPage.requestDirectProviderTransaction(plan.instruction);
        var h$2 = Utils$OrcaPaymentPage.getString(Utils$OrcaPaymentPage.getDictFromJson(res$1), "txHash", "");
        match$1 = [
          h$2,
          h$2 === "" ? "Transaction submitted." : "Transaction submitted: " + h$2
        ];
      }
      catch (raw_e$1){
        var e$1 = Caml_js_exceptions.internalToOCamlException(raw_e$1);
        console.error("NativeCryptoDisplay route execution:", e$1);
        setRouteExecutionMessage(function (param) {
              return "Wallet execution failed. Check wallet connection and network.";
            });
        setIsExecutingRoute(function (param) {
              return false;
            });
        match$1 = [
          "",
          ""
        ];
      }
    } else {
      var errMsg$1 = Core__Option.flatMap(resultDict$1["error"], Core__JSON.Decode.string);
      if (errMsg$1 !== undefined) {
        setRouteExecutionMessage(function (param) {
              return errMsg$1;
            });
        setIsExecutingRoute(function (param) {
              return false;
            });
        match$1 = [
          "",
          ""
        ];
      } else {
        var h$3 = Utils$OrcaPaymentPage.getString(resultDict$1, "txHash", "");
        match$1 = [
          h$3,
          h$3 === "" ? "Transaction submitted." : "Transaction submitted: " + h$3
        ];
      }
    }
    var msg = match$1[1];
    var txHash$1 = match$1[0];
    if (msg !== "") {
      setRouteExecutionMessage(function (param) {
            return msg;
          });
      if (txHash$1 !== "") {
        try {
          await submitCryptoActionTx(clientSecret, publishableKey, paymentId, profileId, plan.actionId, txHash$1, handoffToken, handoffMerchantId, apiEndpoint);
        }
        catch (raw_submitError$1){
          var submitError$1 = Caml_js_exceptions.internalToOCamlException(raw_submitError$1);
          console.error("NativeCryptoDisplay route submit:", submitError$1);
          setRouteExecutionMessage(function (param) {
                return msg + " Backend sync is pending.";
              });
        }
      }
      
    }
    return setIsExecutingRoute(function (param) {
                return false;
              });
  };
  var steps = [
    "Pending",
    "Detected",
    "Confirming",
    "Confirmed"
  ];
  var bannerTestMode = publishableKey.startsWith("pk_snd_");
  var tmp;
  if (routePlanInstruction !== undefined && (routePlanInstruction.sourceChain !== "" || routePlanInstruction.sourceAsset !== "")) {
    var sourceDecimals = Core__Option.getOr(routePlanInstruction.sourceTokenDecimals, decimals);
    tmp = React.createElement("div", {
          className: "w-full flex flex-col gap-1.5"
        }, React.createElement("div", {
              className: "text-[11px] text-nd_gray-500 leading-relaxed"
            }, "You pay"), React.createElement("div", {
              className: "flex items-baseline gap-1.5"
            }, React.createElement("span", {
                  className: "text-base font-semibold text-nd_gray-900"
                }, formatAmount(routePlanInstruction.sourceAmountMinor, sourceDecimals)), React.createElement("span", {
                  className: "text-sm font-medium text-nd_gray-700"
                }, routePlanInstruction.sourceAsset + " on " + formatChainName(routePlanInstruction.sourceChain))), React.createElement("div", {
              className: "text-[11px] text-nd_gray-500 leading-relaxed"
            }, "≈ settles in " + settlementAsset + " on " + formatChainName(settlementChain)), React.createElement(RenderIf$OrcaPaymentPage.make, {
              condition: routePlanInstruction.feeBearer === "merchant",
              children: React.createElement("span", {
                    className: "self-start inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-nd_gray-100 text-[10px] font-medium text-nd_gray-600"
                  }, "含转换费,商户承担")
            }), React.createElement(RenderIf$OrcaPaymentPage.make, {
              condition: routePlanInstruction.feeBearer === "buyer",
              children: React.createElement("span", {
                    className: "self-start inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-nd_gray-100 text-[10px] font-medium text-nd_gray-600"
                  }, "含转换费,买家承担")
            }));
  } else {
    tmp = null;
  }
  var tmp$1;
  if (routePlanInstruction !== undefined && !showRailSelector) {
    var eps$1 = routePlanInstruction.executionPlan;
    var eps$2 = routePlanInstruction.executionPlan;
    var tmp$2;
    if (eps$2 !== undefined) {
      var activeStepCount = Belt_Array.keep(eps$2, (function (s) {
              if (s.kind === "transaction" && s.status === "incomplete") {
                return Utils$OrcaPaymentPage.getString(Utils$OrcaPaymentPage.getDictFromJson(s.call), "mode", "") !== "deposit_address";
              } else {
                return false;
              }
            })).length;
      var allDone = stepResults.length > 0 && Belt_Array.every(stepResults, (function (param) {
              if (Core__Option.isNone(param[3])) {
                if (param[1] !== "") {
                  return true;
                } else {
                  return param[2];
                }
              } else {
                return false;
              }
            }));
      tmp$2 = React.createElement("button", {
            className: "w-full py-3 rounded-xl text-sm font-semibold text-white bg-nd_primary_blue transition-opacity " + (
              isExecutingRoute || allDone || activeStepCount === 0 ? "opacity-60 cursor-not-allowed" : "hover:opacity-90"
            ),
            disabled: isExecutingRoute || allDone || activeStepCount === 0,
            onClick: (function (param) {
                handleExecuteRoute(routePlanInstruction);
              })
          }, activeStepCount === 0 ? "No pending transaction steps" : (
              allDone ? "All steps completed" : (
                  isExecutingRoute ? "Executing steps…" : "Execute " + activeStepCount.toString() + " step" + (
                      activeStepCount === 1 ? "" : "s"
                    )
                )
            ));
    } else {
      tmp$2 = React.createElement("button", {
            className: "w-full py-3 rounded-xl text-sm font-semibold text-white bg-nd_primary_blue transition-opacity " + (
              isExecutingRoute || routePlanInstruction.stepId === "" ? "opacity-60 cursor-not-allowed" : "hover:opacity-90"
            ),
            disabled: isExecutingRoute || routePlanInstruction.stepId === "",
            onClick: (function (param) {
                handleExecuteRoute(routePlanInstruction);
              })
          }, routePlanInstruction.stepId === "" ? "Preparing route…" : (
              isExecutingRoute ? "Opening wallet…" : "Sign route transaction"
            ));
    }
    tmp$1 = React.createElement("div", {
          className: "w-full border border-nd_gray-200 rounded-2xl bg-white p-4 flex flex-col gap-3 shadow-sm"
        }, React.createElement("div", {
              className: "flex items-center justify-between gap-2"
            }, React.createElement("div", undefined, React.createElement("div", {
                      className: "text-sm font-semibold text-nd_gray-900"
                    }, "Cross-chain payment"), React.createElement("div", {
                      className: "text-[11px] text-nd_gray-400 mt-0.5"
                    }, routePlanInstruction.sourceAsset + " on " + routePlanInstruction.sourceChain)), React.createElement("div", {
                  className: "text-[11px] font-medium px-2.5 py-1 rounded-full bg-nd_gray-100 text-nd_gray-600 capitalize"
                }, routePlanInstruction.status)), React.createElement("div", {
              className: "grid grid-cols-2 gap-2 text-[11px]"
            }, React.createElement("div", {
                  className: "rounded-xl bg-nd_gray-50 p-2.5"
                }, React.createElement("div", {
                      className: "text-nd_gray-400 uppercase tracking-wide text-[10px] font-medium"
                    }, "Provider"), React.createElement("div", {
                      className: "font-medium text-nd_gray-700 break-all mt-0.5"
                    }, routePlanInstruction.provider === "" ? "pending" : routePlanInstruction.provider)), React.createElement("div", {
                  className: "rounded-xl bg-nd_gray-50 p-2.5"
                }, React.createElement("div", {
                      className: "text-nd_gray-400 uppercase tracking-wide text-[10px] font-medium"
                    }, "Step"), React.createElement("div", {
                      className: "font-medium text-nd_gray-700 break-all mt-0.5"
                    }, routePlanInstruction.stepStatus))), React.createElement(RenderIf$OrcaPaymentPage.make, {
              condition: isExecutingRoute && executingStepIndex >= 0,
              children: React.createElement("div", {
                    className: "w-full flex items-center gap-2 rounded-md bg-blue-50 border border-blue-200 px-3 py-2 text-[11px] text-blue-700 font-medium"
                  }, React.createElement("span", {
                        className: "animate-spin inline-block w-3 h-3 border-2 border-blue-500 border-t-transparent rounded-full shrink-0"
                      }), React.createElement("span", undefined, "Step " + (executingStepIndex + 1 | 0).toString() + "/" + match$41[0].toString() + " · " + match$42[0]))
            }), eps$1 !== undefined ? React.createElement("div", {
                className: "flex flex-col gap-1.5"
              }, eps$1.map(function (step, i) {
                    var stepResult = stepResults[i];
                    var resultTxHash = stepResult !== undefined ? stepResult[1] : "";
                    var resultSkipped = stepResult !== undefined ? stepResult[2] : false;
                    var resultError = stepResult !== undefined ? stepResult[3] : undefined;
                    var isDone = step.status === "complete" || resultTxHash !== "" && !resultSkipped || resultSkipped && Core__Option.isNone(resultError);
                    var isFailed = Core__Option.isSome(resultError) && !resultSkipped;
                    var isExecuting = isExecutingRoute && !isDone && !isFailed && executingStepIndex === i;
                    var isSolanaStub = resultSkipped && Core__Option.isNone(resultError) && resultTxHash === "" && Utils$OrcaPaymentPage.getString(Utils$OrcaPaymentPage.getDictFromJson(step.call), "mode", "") === "solana_instructions";
                    var isDepositAddress = Utils$OrcaPaymentPage.getString(Utils$OrcaPaymentPage.getDictFromJson(step.call), "mode", "") === "deposit_address";
                    var icon = isDepositAddress ? "@" : (
                        isSolanaStub ? "◎" : (
                            isDone ? "✓" : (
                                isFailed ? "✗" : (
                                    isExecuting ? "→" : (
                                        step.kind === "signature" ? "✎" : "○"
                                      )
                                  )
                              )
                          )
                      );
                    var iconColor = isDepositAddress ? "#2563eb" : (
                        isSolanaStub ? "#9333ea" : (
                            isDone ? "#10b981" : (
                                isFailed ? "#ef4444" : (
                                    isExecuting ? "#3b82f6" : "#9ca3af"
                                  )
                              )
                          )
                      );
                    return React.createElement("div", {
                                key: step.id + i.toString(),
                                className: "flex items-start gap-2 rounded-md p-2 text-[11px] leading-relaxed " + (
                                  isExecuting ? "bg-blue-50" : (
                                      isSolanaStub ? "bg-purple-50" : "bg-nd_gray-50"
                                    )
                                )
                              }, React.createElement("div", {
                                    className: "font-mono font-bold pt-0.5 shrink-0",
                                    style: {
                                      color: iconColor
                                    }
                                  }, icon), React.createElement("div", {
                                    className: "flex-1 min-w-0"
                                  }, React.createElement("div", {
                                        className: "font-medium text-nd_gray-800 break-all"
                                      }, step.action), React.createElement(RenderIf$OrcaPaymentPage.make, {
                                        condition: step.description !== "",
                                        children: React.createElement("div", {
                                              className: "text-nd_gray-400 mt-0.5 break-all"
                                            }, step.description)
                                      }), React.createElement(RenderIf$OrcaPaymentPage.make, {
                                        condition: step.kind === "signature" && !isSolanaStub,
                                        children: React.createElement("div", {
                                              className: "text-nd_gray-400 mt-0.5"
                                            }, "(wallet signature requested — no broadcast)")
                                      }), React.createElement(RenderIf$OrcaPaymentPage.make, {
                                        condition: isDepositAddress,
                                        children: React.createElement("div", {
                                              className: "text-blue-600 mt-0.5 text-[10px] leading-relaxed"
                                            }, "Send funds to the address below. No wallet transaction is required here.")
                                      }), React.createElement(RenderIf$OrcaPaymentPage.make, {
                                        condition: isSolanaStub,
                                        children: React.createElement("div", {
                                              className: "text-purple-600 mt-0.5 text-[10px] leading-relaxed"
                                            }, "Solana wallet support coming — complete this step in Phantom.")
                                      }), React.createElement(RenderIf$OrcaPaymentPage.make, {
                                        condition: resultTxHash !== "" && !resultSkipped,
                                        children: React.createElement("div", {
                                              className: "font-mono text-[10px] text-nd_gray-500 mt-1 break-all"
                                            }, "tx: ", resultTxHash)
                                      }), React.createElement(RenderIf$OrcaPaymentPage.make, {
                                        condition: isFailed,
                                        children: React.createElement("div", {
                                              className: "text-red-500 mt-1 break-all"
                                            }, Core__Option.getOr(resultError, ""))
                                      })), React.createElement(RenderIf$OrcaPaymentPage.make, {
                                    condition: isDone && !isSolanaStub,
                                    children: React.createElement("div", {
                                          className: "text-[10px] px-1.5 py-0.5 rounded bg-green-100 text-green-700 font-medium shrink-0"
                                        }, "Done")
                                  }), React.createElement(RenderIf$OrcaPaymentPage.make, {
                                    condition: isSolanaStub,
                                    children: React.createElement("div", {
                                          className: "text-[10px] px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 font-medium shrink-0"
                                        }, "Pending")
                                  }));
                  })) : React.createElement("pre", {
                className: "w-full max-h-36 overflow-auto rounded-md bg-nd_gray-50 p-2 text-[10px] leading-relaxed text-nd_gray-700 whitespace-pre-wrap break-all"
              }, JSON.stringify(routePlanInstruction.instruction)), React.createElement(RenderIf$OrcaPaymentPage.make, {
              condition: Core__Option.isSome(routeExecutionMessage),
              children: React.createElement("div", {
                    className: "text-[11px] text-nd_gray-600 break-all leading-relaxed"
                  }, Core__Option.getOr(routeExecutionMessage, ""))
            }), React.createElement(RenderIf$OrcaPaymentPage.make, {
              condition: !hasDepositAddressInstruction,
              children: tmp$2
            }));
  } else {
    tmp$1 = null;
  }
  var tmp$3;
  tmp$3 = invoiceStatus === "Confirming" && confirmationProgress !== undefined ? React.createElement("div", {
          className: "text-[11px] text-center mt-1 text-nd_gray-500"
        }, confirmationProgress[0].toString() + " / " + confirmationProgress[1].toString() + " confirmations") : null;
  return React.createElement(Modal$OrcaPaymentPage.make, {
              children: React.createElement("div", {
                    className: "flex flex-col items-center p-6 gap-5 max-w-sm mx-auto"
                  }, React.createElement("div", {
                        className: "w-full flex flex-col items-center text-center gap-2"
                      }, React.createElement("div", {
                            className: "text-[11px] font-medium uppercase tracking-wide text-nd_gray-400"
                          }, hasRoutePlanInstruction ? "Authorize payment" : "Amount to send"), React.createElement("div", {
                            className: "flex items-baseline justify-center gap-2"
                          }, React.createElement("div", {
                                className: "text-3xl font-bold text-nd_gray-900 leading-none tracking-tight"
                              }, amountDisplay), React.createElement("div", {
                                className: "text-lg font-semibold text-nd_gray-600"
                              }, asset)), React.createElement("div", {
                            className: "flex items-center justify-center gap-2 mt-1"
                          }, React.createElement("span", {
                                className: "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-nd_gray-100 text-[11px] font-medium text-nd_gray-700"
                              }, React.createElement("span", {
                                    className: "w-1.5 h-1.5 rounded-full bg-nd_primary_blue"
                                  }), formatChainName(chain) + " network"), React.createElement(RenderIf$OrcaPaymentPage.make, {
                                condition: !isTerminal,
                                children: React.createElement("span", {
                                      className: "inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-nd_gray-50 text-[11px] font-medium text-nd_gray-600"
                                    }, "Expires " + expiryString)
                              }))), React.createElement(RenderIf$OrcaPaymentPage.make, {
                        condition: showRailSelector,
                        children: React.createElement("div", {
                              className: "w-full border border-nd_gray-200 rounded-2xl bg-white p-4 flex flex-col gap-3 shadow-sm"
                            }, React.createElement("div", undefined, React.createElement("div", {
                                      className: "text-sm font-semibold text-nd_gray-900"
                                    }, "Pay with"), React.createElement("div", {
                                      className: "text-[11px] text-nd_gray-500 mt-0.5 leading-relaxed"
                                    }, "Choose what to pay with. We convert to what the merchant settles in.")), React.createElement("div", {
                                  className: "grid grid-cols-1 gap-2"
                                }, railOptions.map(function (opt, i) {
                                      var a = opt[1];
                                      var c = opt[0];
                                      var isSelected = effectiveRail !== undefined ? effectiveRail[0] === c && effectiveRail[1] === a : false;
                                      var isDefault = i === 0;
                                      return React.createElement("button", {
                                                  key: c + "-" + a + "-" + i.toString(),
                                                  className: "w-full flex items-center justify-between gap-2 py-2.5 px-3 rounded-lg text-sm font-semibold border transition-colors " + (
                                                    isSelected ? "border-nd_primary_blue bg-nd_primary_blue text-white" : "border-nd_gray-200 text-nd_gray-700 hover:bg-nd_gray-50"
                                                  ),
                                                  onClick: (function (param) {
                                                      setSelectedRail(function (param) {
                                                            return opt;
                                                          });
                                                    })
                                                }, React.createElement("span", undefined, railLabel(opt)), React.createElement(RenderIf$OrcaPaymentPage.make, {
                                                      condition: isDefault,
                                                      children: React.createElement("span", {
                                                            className: "text-[10px] font-medium px-1.5 py-0.5 rounded " + (
                                                              isSelected ? "bg-white/20 text-white" : "bg-nd_gray-100 text-nd_gray-500"
                                                            )
                                                          }, "Default")
                                                    }));
                                    })), React.createElement("button", {
                                  className: "w-full py-3 rounded-xl text-sm font-semibold text-white bg-nd_primary_blue hover:opacity-90 transition-opacity",
                                  onClick: handleConfirmPay
                                }, "Continue to payment"))
                      }), React.createElement(RenderIf$OrcaPaymentPage.make, {
                        condition: isConversion && !isTerminal && !showRailSelector,
                        children: React.createElement("div", {
                              className: "w-full border border-nd_gray-200 rounded-2xl bg-white p-4 flex flex-col gap-2 shadow-sm"
                            }, React.createElement("div", {
                                  className: "flex items-center justify-between gap-2"
                                }, React.createElement("div", {
                                      className: "text-sm font-semibold text-nd_gray-900"
                                    }, "Quote"), React.createElement(RenderIf$OrcaPaymentPage.make, {
                                      condition: !quoteExpired && quoteExpiryTime > 0.0,
                                      children: React.createElement("span", {
                                            className: "inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-nd_gray-50 text-[11px] font-medium text-nd_gray-600"
                                          }, "Locked " + quoteExpiryString)
                                    })), tmp, React.createElement(RenderIf$OrcaPaymentPage.make, {
                                  condition: quoteExpired,
                                  children: React.createElement("div", {
                                        className: "w-full flex flex-col gap-2 rounded-xl bg-amber-50 border border-amber-200 p-3"
                                      }, React.createElement("div", {
                                            className: "text-[11px] text-amber-700 font-medium leading-relaxed"
                                          }, "Quote expired — the price lock ran out. Refresh to get a new rate. Your invoice is still valid."), React.createElement("button", {
                                            className: "w-full py-2.5 rounded-lg text-sm font-semibold text-white bg-nd_primary_blue hover:opacity-90 transition-opacity",
                                            onClick: (function (param) {
                                                handleRefreshQuote();
                                              })
                                          }, "Refresh quote"))
                                }))
                      }), React.createElement(RenderIf$OrcaPaymentPage.make, {
                        condition: showMobileIframeContinue,
                        children: React.createElement("div", {
                              className: "w-full border border-nd_gray-200 rounded-2xl bg-white p-4 flex flex-col gap-3 shadow-sm"
                            }, React.createElement("div", {
                                  className: "text-sm font-semibold text-nd_gray-900"
                                }, "Continue payment"), React.createElement("div", {
                                  className: "text-[11px] text-nd_gray-500 leading-relaxed"
                                }, "Open the secure handoff page on this device. QR is hidden here because you cannot scan your own screen."), React.createElement("button", {
                                  className: "w-full py-3 rounded-xl text-sm font-semibold text-white bg-nd_primary_blue hover:opacity-90 transition-opacity",
                                  onClick: handleContinueOnDevice
                                }, "Continue payment"))
                      }), React.createElement(RenderIf$OrcaPaymentPage.make, {
                        condition: showWalletLauncher,
                        children: React.createElement("div", {
                              className: "w-full border border-nd_gray-200 rounded-2xl bg-white p-4 flex flex-col gap-3 shadow-sm"
                            }, React.createElement("div", undefined, React.createElement("div", {
                                      className: "text-sm font-semibold text-nd_gray-900"
                                    }, "Open in a wallet"), React.createElement("div", {
                                      className: "text-[11px] text-nd_gray-500 mt-1 leading-relaxed"
                                    }, "Choose a wallet to reopen this same handoff page inside its browser.")), React.createElement("div", {
                                  className: "grid grid-cols-1 gap-2"
                                }, CryptoCheckoutEnvironment$OrcaPaymentPage.walletLinks(CryptoCheckoutEnvironment$OrcaPaymentPage.currentHref()).map(function (link) {
                                      var linkDict = Utils$OrcaPaymentPage.getDictFromJson(link);
                                      var label = Utils$OrcaPaymentPage.getString(linkDict, "label", "Wallet");
                                      var href = Utils$OrcaPaymentPage.getString(linkDict, "href", "");
                                      return React.createElement("button", {
                                                  key: label,
                                                  className: "w-full py-2.5 rounded-lg text-sm font-semibold border border-nd_primary_blue text-nd_primary_blue hover:bg-nd_primary_blue hover:text-white transition-colors",
                                                  onClick: (function (param) {
                                                      CryptoCheckoutEnvironment$OrcaPaymentPage.continueOnTop(href);
                                                    })
                                                }, "Open " + label);
                                    })))
                      }), tmp$1, React.createElement(RenderIf$OrcaPaymentPage.make, {
                        condition: qrData !== "" && !isTerminal && !showRailSelector && (showHandoffQr || directTransferVisible) && !showMobileIframeContinue && !showWalletLauncher,
                        children: React.createElement("div", {
                              className: "w-full border border-nd_gray-200 rounded-2xl p-6 bg-white shadow-sm flex flex-col items-center gap-5"
                            }, React.createElement("img", {
                                  style: {
                                    height: "200px",
                                    width: "200px"
                                  },
                                  alt: showHandoffQr ? "handoff payment QR" : "receive address QR",
                                  src: qrSrc
                                }), React.createElement(RenderIf$OrcaPaymentPage.make, {
                                  condition: !showHandoffQr && hasWalletNativeQr && !isSolanaChain(chain),
                                  children: React.createElement("button", {
                                        className: "text-[11px] font-semibold px-3 py-1.5 rounded-lg border border-nd_gray-200 text-nd_gray-700 hover:bg-nd_gray-50 transition-colors",
                                        onClick: (function (param) {
                                            setUseWalletNativeQr(function (prev) {
                                                  return !prev;
                                                });
                                          })
                                      }, useWalletNativeQr ? "Show plain address QR" : "Try wallet-native QR")
                                }), React.createElement(RenderIf$OrcaPaymentPage.make, {
                                  condition: !showHandoffQr && !isSolanaChain(chain),
                                  children: React.createElement("div", {
                                        className: "w-full flex flex-col items-center gap-0.5 text-center text-[11px] text-nd_gray-500 leading-relaxed"
                                      }, React.createElement("div", undefined, "扫不出就复制地址手动发送（" + formatChainName(chain) + " · " + asset + "）"), React.createElement("div", undefined, "If your wallet can't scan, copy the address and send manually on " + formatChainName(chain) + "."))
                                }), React.createElement("div", {
                                  className: "w-full flex items-center gap-2 rounded-xl bg-nd_gray-50 border border-nd_gray-100 px-3 py-2.5"
                                }, React.createElement("div", {
                                      className: "flex-1 min-w-0 font-mono text-[10px] text-nd_gray-700 whitespace-nowrap overflow-hidden select-all"
                                    }, address), React.createElement("button", {
                                      className: "shrink-0 text-[11px] font-semibold px-3 py-1.5 rounded-lg border border-nd_primary_blue text-nd_primary_blue hover:bg-nd_primary_blue hover:text-white transition-colors",
                                      onClick: handleCopy
                                    }, match$5[0] ? "Copied!" : "Copy")), React.createElement("div", {
                                  className: "w-full flex items-center gap-2 rounded-xl bg-nd_gray-50 border border-nd_gray-100 px-3 py-2.5"
                                }, React.createElement("div", {
                                      className: "flex-1 min-w-0 text-[11px] text-nd_gray-700"
                                    }, amountDisplay + " " + asset), React.createElement("button", {
                                      className: "shrink-0 text-[11px] font-semibold px-3 py-1.5 rounded-lg border border-nd_primary_blue text-nd_primary_blue hover:bg-nd_primary_blue hover:text-white transition-colors",
                                      onClick: handleCopyAmount
                                    }, "Copy amount")))
                      }), React.createElement(RenderIf$OrcaPaymentPage.make, {
                        condition: (showWalletLauncher || hasRoutePlanInstruction) && address !== "" && !isTerminal,
                        children: React.createElement("div", {
                              className: "w-full border border-nd_gray-200 rounded-2xl bg-white p-4 flex flex-col gap-2 shadow-sm"
                            }, React.createElement("div", {
                                  className: "text-sm font-semibold text-nd_gray-900"
                                }, "Use direct transfer instead"), React.createElement("div", {
                                  className: "text-[11px] text-nd_gray-500 leading-relaxed"
                                }, "Copy the address and amount if you are paying from an exchange or cold wallet."), React.createElement("div", {
                                  className: "flex items-center gap-2 rounded-xl bg-nd_gray-50 border border-nd_gray-100 px-3 py-2.5"
                                }, React.createElement("div", {
                                      className: "flex-1 min-w-0 font-mono text-[10px] text-nd_gray-700 whitespace-nowrap overflow-hidden select-all"
                                    }, address), React.createElement("button", {
                                      className: "shrink-0 text-[11px] font-semibold px-3 py-1.5 rounded-lg border border-nd_primary_blue text-nd_primary_blue hover:bg-nd_primary_blue hover:text-white transition-colors",
                                      onClick: handleCopy
                                    }, "Copy")), React.createElement("div", {
                                  className: "flex items-center gap-2 rounded-xl bg-nd_gray-50 border border-nd_gray-100 px-3 py-2.5"
                                }, React.createElement("div", {
                                      className: "flex-1 min-w-0 text-[11px] text-nd_gray-700"
                                    }, amountDisplay + " " + asset), React.createElement("button", {
                                      className: "shrink-0 text-[11px] font-semibold px-3 py-1.5 rounded-lg border border-nd_primary_blue text-nd_primary_blue hover:bg-nd_primary_blue hover:text-white transition-colors",
                                      onClick: handleCopyAmount
                                    }, "Copy amount")))
                      }), React.createElement(RenderIf$OrcaPaymentPage.make, {
                        condition: showSolanaPay,
                        children: React.createElement("div", {
                              className: "w-full flex flex-col gap-2"
                            }, React.createElement("button", {
                                  className: "w-full py-3 rounded-xl text-sm font-semibold text-white bg-nd_primary_blue transition-opacity " + (
                                    isExecutingSolana || solanaSignature !== "" ? "opacity-60 cursor-not-allowed" : "hover:opacity-90"
                                  ),
                                  disabled: isExecutingSolana || solanaSignature !== "",
                                  onClick: (function (param) {
                                      handleExecuteSolana();
                                    })
                                }, solanaSignature !== "" ? "Sent — awaiting confirmation…" : (
                                    isExecutingSolana ? "Opening Solana wallet…" : "Pay with Solana wallet"
                                  )), React.createElement(RenderIf$OrcaPaymentPage.make, {
                                  condition: Core__Option.isSome(solanaMessage),
                                  children: React.createElement("div", {
                                        className: "text-[11px] text-nd_gray-600 break-all leading-relaxed text-center"
                                      }, Core__Option.getOr(solanaMessage, ""))
                                }), React.createElement("div", {
                                  className: "text-[10px] text-nd_gray-400 text-center leading-relaxed"
                                }, "Or scan the QR code above with Phantom / Solflare / Backpack."))
                      }), React.createElement(RenderIf$OrcaPaymentPage.make, {
                        condition: invoiceStatus === "Paid",
                        children: React.createElement("div", {
                              className: "flex flex-col items-center gap-3 py-2"
                            }, React.createElement("div", {
                                  className: "w-16 h-16 rounded-full flex items-center justify-center text-3xl text-white",
                                  style: {
                                    backgroundColor: "#10b981"
                                  }
                                }, "✓"), React.createElement("div", {
                                  className: "text-base font-semibold",
                                  style: {
                                    color: "#10b981"
                                  }
                                }, "Payment confirmed!"))
                      }), React.createElement("div", {
                        className: "w-full"
                      }, React.createElement("div", {
                            className: "relative px-4"
                          }, React.createElement("div", {
                                className: "absolute left-4 right-4 top-4 h-0.5 bg-nd_gray-200 rounded-full"
                              }), React.createElement("div", {
                                className: "absolute left-4 top-4 h-0.5 rounded-full transition-all",
                                style: {
                                  backgroundColor: statusColor,
                                  width: "calc((100% - 2rem) * " + (step / 3.0).toString() + ")"
                                }
                              }), React.createElement("div", {
                                className: "flex justify-between items-start relative"
                              }, steps.map(function (label, i) {
                                    var active = i <= step;
                                    var isCurrent = i === step;
                                    return React.createElement("div", {
                                                key: label,
                                                className: "flex flex-col items-center gap-1.5 flex-1"
                                              }, React.createElement("div", {
                                                    className: "w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold border-2 transition-colors " + (
                                                      isCurrent && !isTerminal ? "animate-pulse ring-4 ring-nd_gray-100" : ""
                                                    ),
                                                    style: {
                                                      backgroundColor: active ? statusColor : "#ffffff",
                                                      borderColor: active ? statusColor : "#e5e7eb",
                                                      color: active ? "#ffffff" : "#9ca3af"
                                                    }
                                                  }, active && i < step ? "✓" : (i + 1 | 0).toString()), React.createElement("div", {
                                                    className: "text-[10px] text-center " + (
                                                      active ? "font-semibold" : "text-nd_gray-400"
                                                    ),
                                                    style: {
                                                      color: active ? statusColor : "#9ca3af"
                                                    }
                                                  }, label));
                                  }))), React.createElement("div", {
                            className: "text-xs text-center mt-3 font-medium",
                            style: {
                              color: statusColor
                            }
                          }, statusLabel, React.createElement(RenderIf$OrcaPaymentPage.make, {
                                condition: !isTerminal,
                                children: React.createElement("span", {
                                      className: "ml-1 animate-pulse"
                                    }, "●")
                              })), tmp$3), React.createElement(RenderIf$OrcaPaymentPage.make, {
                        condition: Core__Option.isSome(submitError),
                        children: React.createElement("div", {
                              className: "text-xs text-center",
                              style: {
                                color: "#ef4444"
                              }
                            }, Core__Option.getOr(submitError, ""))
                      }), React.createElement(RenderIf$OrcaPaymentPage.make, {
                        condition: !isTerminal && !showRailSelector && (!hasRoutePlanInstruction || hasDepositAddressInstruction),
                        children: React.createElement("div", {
                              className: "w-full flex flex-col gap-3"
                            }, React.createElement("div", {
                                  className: "text-[11px] text-nd_gray-400 text-center leading-relaxed px-2"
                                }, "Send exactly " + amountDisplay + " " + asset + " to the address above. This page updates automatically — do not close."), React.createElement("button", {
                                  className: "w-full py-3 rounded-xl text-sm font-semibold text-white bg-nd_primary_blue transition-opacity " + (
                                    isSubmitting ? "opacity-60 cursor-not-allowed" : "hover:opacity-90"
                                  ),
                                  disabled: isSubmitting,
                                  onClick: (function (param) {
                                      handleDone();
                                    })
                                }, isSubmitting ? "Confirming…" : "I've sent the payment"))
                      }), React.createElement(RenderIf$OrcaPaymentPage.make, {
                        condition: isTerminal && invoiceStatus !== "Paid",
                        children: React.createElement("button", {
                              className: "w-full py-3 rounded-xl text-sm font-semibold text-nd_gray-600 border border-nd_gray-200 hover:bg-nd_gray-50 transition-colors",
                              onClick: (function (param) {
                                  Modal$OrcaPaymentPage.close(setOpenModal);
                                })
                            }, "Close")
                      })),
              showClose: false,
              testMode: bannerTestMode,
              setOpenModal: setOpenModal,
              openModal: openModal
            });
}

var maxPollMs = 900000.0;

var pollIntervalMs = 4000;

var make = NativeCryptoDisplay;

var $$default = NativeCryptoDisplay;

export {
  maxPollMs ,
  pollIntervalMs ,
  parseInvoiceStatus ,
  invoiceStatusLabel ,
  invoiceStatusColor ,
  invoiceStatusStep ,
  isTerminalStatus ,
  isSolanaChain ,
  normalizeIsoUtc ,
  getTimestampSeconds ,
  parseExecutionPlan ,
  parseCryptoActionInstruction ,
  parseSolanaTransferActionId ,
  parseAvailableRails ,
  formatAmount ,
  formatChainName ,
  qrImageUrl ,
  fetchInstructionStatus ,
  submitCryptoActionTx ,
  fetchSolanaPayTransaction ,
  refreshCryptoQuote ,
  safeDetectEnvironment ,
  make ,
  $$default as default,
}
/* react Not a pure module */
